import React, { use, useEffect, useReducer, useState } from 'react';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import CallOut from '../../Common/CallOut';
import TextInput from '../../Common/TextInput';
import ActivityLoader from '../../Common/ActivityLoader';
import ajaxConfig from '../../../util/remote/ajaxConfig';
import { useTheme } from '../../../DataStore/ThemeContext';

const initialState = {
  first_name: '',
  last_name: '',
  designation: '',
  status: '1',
  loading: false,
};

const reducer = (state, action) => {
  if (action.type === 'RESET') return { ...initialState };
  return { ...state, [action.type]: action.value };
};

const CreateSignatory = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [notify, setNotify] = useState(null);
  const { getSignatories } = useConfig();
  const { setSystemFunction } = useTheme();

  useEffect(() => {
    if (!notify) return;
    const t = setTimeout(() => setNotify(null), 4500);
    return () => clearTimeout(t);
  }, [notify]);

  useEffect(() => {
    setSystemFunction(() => handleSave);
    return () => setSystemFunction(() => {});
  }, [state]);

  const onChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: name, value });
  };

  const validate = () => {
    if (!state.first_name.trim()) return 'First name is required.';
    if (!state.last_name.trim()) return 'Last name is required.';
    if (!state.designation.trim()) return 'Designation is required.';
    if (!state.status) return 'Status is required.';
    return null;
  };


  const RenderStatusSelect = () => {
    const options = [
      { label: 'Active',  value: '1' },
      { label: 'Inactive', value: '0' },
    ];

    return (
      <TextInput
        label="Status"
        type="select"
        name="status"
        leftIcon="fa fa-flag"
        placeholder={'Select Status…'}
        options={options}
        onChange={(item) => dispatch({ type: 'status', value: item })}
        leftComponent={<i className="fa fa-info-circle" title="Active signatories appear on report signature blocks."></i>}
        loading={false}
        wrap
        value={state.status}
      />
    );
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setNotify(<Notification type="danger" message={error} />);
      return;
    }

    try {
      dispatch({ type: 'loading', value: true });

      // API payload matching your signatory shape
      const payload = {
        first_name: state.first_name.trim(),
        last_name: state.last_name.trim(),
        position: state.designation.trim(),
        status: state.status.value, // "Active" | "Inactive"
      };

      // Implement this endpoint server-side (or adjust name if you already have it)
      const res = await ajaxConfig.createSignatory(payload);

      if (res?.status === 'OK') {
        setNotify(<Notification type="success" message={res.message || 'Signatory added.'} />);
        dispatch({ type: 'RESET' });
        getSignatories(true); // refresh the list in settings
      } else {
        setNotify(<Notification type="danger" message={res?.message || 'Failed to add signatory.'} />);
      }
    } catch (err) {
      setNotify(<Notification type="danger" message={String(err?.message || err)}/>);
    } finally {
      dispatch({ type: 'loading', value: false });
    }
  };

  return (
    <>
      <CallOut
        title="New Report Signatory"
        description="Add a person who is authorized to sign official reports. Use their official designation as it will appear on printed statements."
      />
      <div className='mt-4'>{notify}</div>

      <form className="form-horizontal" onSubmit={handleSave}>
        <div className="row">
          <div className="col-md-6">
            <TextInput
              type="text"
              label="First Name"
              placeholder="First Name"
              leftIcon="fa fa-user"
              value={state.first_name}
              name="first_name"
              onChange={onChange}
              required
              wrap
            />
          </div>
          <div className="col-md-6">
            <TextInput
              type="text"
              label="Last Name"
              placeholder="Last Name"
              leftIcon="fa fa-user"
              value={state.last_name}
              name="last_name"
              onChange={onChange}
              required
              wrap
            />
          </div>
        </div>

        <TextInput
          type="text"
          label="Designation"
          placeholder="e.g. Executive Director"
          leftIcon="fa fa-id-badge"
          value={state.designation}
          name="designation"
          onChange={onChange}
          required
          wrap
        />

        {RenderStatusSelect()}
        <small className="form-text text-muted">
            Active signatories appear on report signature blocks.
        </small>


        {/* Print preview hint for aesthetics */}
        <div className="border rounded p-3 mb-3">
          <div className="small text-muted mb-2">Print preview</div>
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mr-3"
              style={{ width: 48, height: 48, background: '#E9F2FF', color: '#111827', fontWeight: 700 }}
            >
              {`${(state.first_name || 'S').charAt(0)}${(state.last_name || 'N').charAt(0)}`.toUpperCase()}
            </div>
            <div>
              <div className="font-weight-semibold">
                {(state.first_name || '').trim()} {(state.last_name || '').trim()}
              </div>
              <div className="text-muted small" style={{ letterSpacing: '.04em' }}>
                {(state.designation || '').trim() || '—'}
              </div>
              <div className="mt-3" style={{ height: 1, background: '#E5E7EB' }} />
              <div className="d-flex justify-content-between text-muted small pt-1">
                <span>Signature</span>
                <span>Date</span>
              </div>
            </div>
          </div>
        </div>


      </form>
    </>
  );
};

export default CreateSignatory;
